import { createContext, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import GrIcon from "src/design-system/components/GrIcon";
import GrCollapse from "src/design-system/components/GrCollapse";
import icons from "src/design-system/icons";
import { gr } from "src/design-system/tokens";
import { s3baseUrl } from "src/config/config";

// Shared by every workout card of GeneralCalendar (programme calendar + main Calendar page).
// Cards keep their own open state, so a toggle animates in place instead of FullCalendar
// rebuilding every event. "Expand / Collapse all" replaces `allOpen`; `openById` remembers
// single toggles when FullCalendar re-mounts a card (month switch, reload after save).
export const CalendarCardsContext = createContext({
  selectedId: null,
  onOpenWorkout: () => {},
  allOpen: { open: true, version: 0 },
  openById: new Map(),
});

// Same order as before: workout first (a video in a workout library is a workout).
const TYPES = [
  { match: (d) => d.practice_type === "workout" || d.library_type === "workout-type", icon: icons.taskWorkout, label: "Workout" },
  { match: (d) => d.practice_type === "food" || d.library_type === "meal-type", icon: icons.taskMeal, label: "Meal" },
  { match: (d) => d.practice_type === "mindset" || d.library_type === "mindset-type", icon: icons.taskMindset, label: "Mindset" },
  { match: (d) => d.practice_type === "video", icon: icons.taskVideo, label: "Video" },
  { match: (d) => d.practice_type === "comment", icon: icons.taskComment, label: "Comment" },
  { match: (d) => d.practice_type === "workbook", icon: icons.taskWorkbook, label: "Workbook" },
];
const typeOf = (data) =>
  TYPES.find((t) => t.match(data)) || { icon: icons.taskWorkout, label: "Workout" };

const GROUP_LABELS = {
  amrap: "AMRAP",
  rounds_for_time: "Rounds for time",
  reps_for_time: "Reps for time",
  emom: "EMOM",
};

const twoDigits = (value) => (value < 10 ? `0${value}` : `${value}`);

// "Minute 1", "Minute 3 – 4" or "Minute 1 :30 – 2 :00" for one EMOM interval.
const minuteLabel = (interval, index) => {
  if (interval <= 60) return index + 1;
  if (interval % 60 === 0) {
    const step = interval / 60;
    return `${step * index + 1} – ${step * index + step}`;
  }
  const start = interval * index;
  const end = start + interval;
  const startText = start ? `${Math.floor(start / 60)} :${twoDigits(start % 60)}` : 0;
  return `${startText} – ${Math.floor(end / 60)} :${twoDigits(end % 60)}`;
};

const textSx = {
  fontFamily: gr.font.family,
  fontSize: gr.font.size.xs,
  lineHeight: gr.font.lineHeight.tight,
  whiteSpace: "break-spaces",
  wordBreak: "break-word",
};

const lineSx = {
  ...textSx,
  display: "flex",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: gr.space[1],
  padding: `6px ${gr.space[2]}`,
  borderRadius: gr.radius.chip,
  background: gr.color.bg,
  color: gr.color.ink,
  fontWeight: gr.font.weight.medium,
};

const okIconSx = { display: "inline-flex", color: gr.color.ok };

function ExerciseLine({ title, completed, modified }) {
  return (
    <Box sx={lineSx}>
      {completed && (
        <Tooltip title="Completed">
          <Box component="span" sx={okIconSx}>
            <GrIcon icon={icons.checkCircle} size="sm" />
          </Box>
        </Tooltip>
      )}
      <Box component="span" sx={{ flex: "1 1 0", minWidth: 0 }}>
        {title}
      </Box>
      {modified && (
        <Box
          component="span"
          sx={{
            padding: `0 ${gr.space[1]}`,
            borderRadius: gr.radius.pill,
            background: gr.color.dangerSoft,
            color: gr.color.danger,
            fontWeight: gr.font.weight.medium,
          }}
        >
          Modified
        </Box>
      )}
    </Box>
  );
}

ExerciseLine.propTypes = {
  title: PropTypes.node,
  completed: PropTypes.bool,
  modified: PropTypes.bool,
};

// Superset / EMOM / AMRAP…: exercises that belong together, with a teal edge.
function ExerciseGroup({ label, completed, children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: gr.space[1],
        paddingLeft: gr.space[2],
        borderLeft: `2px solid ${gr.color.brand}`,
      }}
    >
      {label && (
        <Box
          sx={{
            ...textSx,
            display: "flex",
            alignItems: "center",
            gap: gr.space[1],
            color: gr.color.brandStrong,
            fontWeight: gr.font.weight.semibold,
          }}
        >
          {completed && (
            <Box component="span" sx={okIconSx}>
              <GrIcon icon={icons.checkCircle} size="sm" title="Completed" />
            </Box>
          )}
          {label}
        </Box>
      )}
      {children}
    </Box>
  );
}

ExerciseGroup.propTypes = {
  label: PropTypes.node,
  completed: PropTypes.bool,
  children: PropTypes.node,
};

const captionSx = { ...textSx, color: gr.color.muted };

function Note({ label, children }) {
  return (
    <Box sx={{ ...textSx, color: gr.color.ink2, lineHeight: gr.font.lineHeight.normal }}>
      <Box
        component="span"
        sx={{ display: "block", color: gr.color.brandStrong, fontWeight: gr.font.weight.semibold }}
      >
        {label}
      </Box>
      {children}
    </Box>
  );
}

Note.propTypes = { label: PropTypes.node, children: PropTypes.node };

const exerciseTitle = (exercise) =>
  exercise?.exercise_type === "something_else"
    ? exercise?.title
    : exercise?.exercise_info?.title;

function MainExercise({ exercise }) {
  const group = GROUP_LABELS[exercise?.exercise_type];
  if (group && exercise?.emom_amrap?.length > 0) {
    return (
      <ExerciseGroup label={group} completed={exercise?.is_completed}>
        {exercise.emom_amrap.map((interval, ii) => (
          <Box key={ii} sx={{ display: "grid", gap: gr.space[1] }}>
            <Box sx={captionSx}>
              {exercise.exercise_type === "emom"
                ? `Minute ${minuteLabel(exercise.interval_duration, ii)}`
                : "Movements"}
            </Box>
            {interval?.map((item, ei) => (
              <ExerciseLine key={ei} title={item?.exercise_info?.title} />
            ))}
          </Box>
        ))}
      </ExerciseGroup>
    );
  }
  if (exercise?.sub_exercises?.length > 0) {
    return (
      <ExerciseGroup>
        {exercise.sub_exercises.map((item, si) => (
          <ExerciseLine
            key={si}
            title={item?.exercise_info?.title}
            completed={item?.is_completed}
            modified={item?.is_modified}
          />
        ))}
      </ExerciseGroup>
    );
  }
  return (
    <ExerciseLine
      title={exerciseTitle(exercise)}
      completed={exercise?.is_completed}
      modified={exercise?.is_modified}
    />
  );
}

MainExercise.propTypes = { exercise: PropTypes.object };

// The video's picture (16:9) with a play mark. No picture, or one that does not load: a slim soft
// strip with the play mark instead of a broken image.
function VideoThumb({ data }) {
  const thumb = data?.video_thumbnail?.thumbnail_1;
  const src = data?.dumyImage || (thumb ? s3baseUrl + thumb : "");
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const showImage = Boolean(src) && !failed;
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: gr.radius.chip,
        background: showImage ? gr.color.bg : gr.color.brandSoft,
        ...(showImage ? { aspectRatio: "16 / 9" } : { height: gr.control.md }),
        "& img": {
          position: "absolute",
          inset: 0,
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
        },
      }}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          width={320}
          height={180}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : null}
      <Box
        component="span"
        sx={{
          position: "absolute",
          inset: 0,
          margin: "auto",
          width: gr.icon.xl,
          height: gr.icon.xl,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: gr.radius.pill,
          background: gr.color.overlay,
          color: gr.color.onBrand,
        }}
      >
        <GrIcon icon={icons.play} size="sm" />
      </Box>
    </Box>
  );
}

VideoThumb.propTypes = { data: PropTypes.object };

export default function CalendarWorkoutCard({ workout }) {
  const data = workout.extendedProps;
  const { selectedId, onOpenWorkout, allOpen, openById } = useContext(CalendarCardsContext);
  const [open, setOpen] = useState(() => openById.get(data._id) ?? allOpen.open);
  const seenAllOpen = useRef(allOpen);
  // Only a card the member opened scrolls itself into view (not every card on "Expand all").
  const [scrollOnOpen, setScrollOnOpen] = useState(false);

  useEffect(() => {
    if (seenAllOpen.current === allOpen) return;
    seenAllOpen.current = allOpen;
    setScrollOnOpen(false);
    setOpen(allOpen.open);
  }, [allOpen]);

  const toggle = () => {
    openById.set(data._id, !open);
    setScrollOnOpen(true);
    setOpen(!open);
  };
  const openWorkout = () => onOpenWorkout(data);
  const onKeyOpen = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openWorkout();
    }
  };

  const type = typeOf(data);
  const accent = data.workout_color || gr.color.brand;
  const selected = Boolean(selectedId) && selectedId === data._id;
  const exercises = data.workout_exercises || [];
  const extra = exercises.filter((exercise) => exercise.is_extra);

  return (
    <Box
      className="calendar-workout-card"
      id={data._id}
      sx={{
        // && beats the legacy .calendar-workout-card rules in style.css.
        "&&": {
          position: "relative",
          overflow: "hidden",
          marginBottom: gr.space[2],
          paddingLeft: "3px",
          fontFamily: gr.font.family,
          fontSize: gr.font.size.xs,
          fontWeight: gr.font.weight.regular,
          background: gr.color.surface,
          border: `1px solid ${selected ? gr.color.brand : gr.color.line}`,
          borderRadius: gr.radius.control,
          boxShadow: selected ? gr.shadow.focus : "none",
          scrollMarginTop: "96px",
          transition: "border-color .15s ease, box-shadow .15s ease",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "3px",
            background: accent,
          },
          "&:hover": {
            borderColor: selected ? gr.color.brand : gr.color.lineStrong,
            boxShadow: selected ? gr.shadow.focus : gr.shadow.card,
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: gr.space[1], padding: gr.space[2] }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={openWorkout}
          onKeyDown={onKeyOpen}
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: "2px",
            cursor: "pointer",
            borderRadius: gr.radius.chip,
            outline: "none",
            "&:focus-visible": { boxShadow: gr.shadow.focus },
            "&:hover .cal-card-title": { color: gr.color.brandStrong },
          }}
        >
          <Box sx={{ ...textSx, display: "flex", alignItems: "center", gap: gr.space[1], color: gr.color.muted, whiteSpace: "nowrap" }}>
            <Box component="span" sx={{ display: "inline-flex", color: accent }}>
              <GrIcon icon={type.icon} size="sm" title={type.label} />
            </Box>
            <Box
              component="span"
              title={data.workout_program?.title}
              sx={{ flex: "1 1 0", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {data.workout_program?.title}
            </Box>
            {data.workout_completed && (
              <Box component="span" sx={okIconSx}>
                <GrIcon icon={icons.checkCircle} size="sm" title="Completed" />
              </Box>
            )}
          </Box>
          <Box
            className="cal-card-title"
            sx={{
              ...textSx,
              fontSize: gr.font.size.sm,
              fontWeight: gr.font.weight.semibold,
              color: gr.color.ink,
              transition: "color .15s ease",
            }}
          >
            {workout.title}
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={open ? "Hide exercises" : "Show exercises"}
          sx={{
            flex: "none",
            width: gr.icon.xl,
            height: gr.icon.xl,
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: 0,
            borderRadius: gr.radius.chip,
            background: "transparent",
            color: gr.color.muted,
            cursor: "pointer",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .24s cubic-bezier(0.2, 0, 0, 1), background-color .15s ease, color .15s ease",
            "&:hover": { background: gr.color.tint, color: gr.color.brandDark },
            "&:focus-visible": { outline: "none", boxShadow: gr.shadow.focus },
          }}
        >
          <GrIcon icon={icons.chevronDown} size="sm" />
        </Box>
      </Box>

      <GrCollapse in={open} scrollOnOpen={scrollOnOpen}>
        <Box
          onClick={openWorkout}
          sx={{
            display: "grid",
            gap: gr.space[1],
            padding: `0 ${gr.space[2]} ${gr.space[2]}`,
            cursor: "pointer",
            "&:empty": { display: "none" },
          }}
        >
          {data.practice_type === "workbook" && <ExerciseLine title={data.workout_title} />}
          {data.practice_type === "comment" && (
            <Box sx={{ ...lineSx, display: "block", fontWeight: gr.font.weight.regular }}>
              {data.comment || ""}
            </Box>
          )}
          {data.practice_type === "video" && <VideoThumb data={data} />}

          {exercises.map((exercise, index) =>
            exercise.is_extra ? null : <MainExercise key={index} exercise={exercise} />,
          )}

          {extra.length > 0 && (
            <Box sx={{ display: "grid", gap: gr.space[1], marginTop: gr.space[1] }}>
              <Box
                sx={{
                  ...textSx,
                  display: "flex",
                  alignItems: "center",
                  gap: gr.space[1],
                  color: gr.color.brandStrong,
                  fontWeight: gr.font.weight.semibold,
                }}
              >
                <GrIcon icon={icons.energy} size="sm" />
                Extra work
              </Box>
              <Box sx={captionSx}>These exercises aren't part of the original workout</Box>
              {extra.map((exercise, index) =>
                exercise?.sub_exercises?.length > 0 ? (
                  <ExerciseGroup key={index}>
                    {exercise.sub_exercises.map((item, si) => (
                      <ExerciseLine key={si} title={item?.exercise_info?.title} />
                    ))}
                  </ExerciseGroup>
                ) : (
                  <ExerciseLine key={index} title={exercise?.exercise_info?.title} />
                ),
              )}
            </Box>
          )}

          {data.practice_type !== "comment" && data.comment && (
            <Note label="Comment">{data.comment}</Note>
          )}
          {data.practice_type !== "comment" && data.admin_comment && (
            <Note label="Reply">{data.admin_comment}</Note>
          )}
        </Box>
      </GrCollapse>
    </Box>
  );
}

CalendarWorkoutCard.propTypes = {
  workout: PropTypes.object.isRequired,
};
